"use client";

import { useState } from "react";

import { Button, SubmitButton } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

import { VARIABLE_CATEGORY_LABELS } from "../../lib/variable-catalog";
import { useVariableContext } from "./context";
import type { VariableAttrs, VariableFallbackPolicy } from "./extension";

const FALLBACK_LITERAL_MAX = 200;

export interface VariableFallbackPopoverProps {
  attrs: VariableAttrs;
  onSave: (next: {
    fallback_policy: VariableFallbackPolicy;
    fallback_literal: string | null;
  }) => void;
  onRemove: () => void;
}

/**
 * Inline popover (chip click target) for editing a variable's fallback policy.
 *
 * Spec: §5.5. Renders below the chip with a draft form — the user picks a
 * policy radio and (when `literal_fallback` is selected) types a literal in a
 * 1–200 char input. Save commits via `onSave`; Remove deletes the node via
 * `onRemove`. Opening the popover with `block_enrollment` keeps the literal
 * draft empty so toggling back-and-forth doesn't surface stale state.
 */
export function VariableFallbackPopover({
  attrs,
  onSave,
  onRemove,
}: VariableFallbackPopoverProps) {
  const { allowBlockEnrollment } = useVariableContext();

  const [policy, setPolicy] = useState<VariableFallbackPolicy>(() =>
    !allowBlockEnrollment && attrs.fallback_policy === "block_enrollment"
      ? "literal_fallback"
      : attrs.fallback_policy,
  );
  const [literal, setLiteral] = useState<string>(attrs.fallback_literal ?? "");
  const [saving, setSaving] = useState(false);

  const literalRequired = policy === "literal_fallback";
  const literalIsValid = !literalRequired || literal.trim().length > 0;
  const canSave = literalIsValid;

  const handleSave = () => {
    if (!canSave) return;
    setSaving(true);
    if (policy === "literal_fallback") {
      onSave({
        fallback_policy: "literal_fallback",
        fallback_literal: literal,
      });
    } else {
      onSave({ fallback_policy: "block_enrollment", fallback_literal: null });
    }
  };

  return (
    <div className="flex w-[320px] flex-col gap-4 p-4">
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-medium text-heading">{attrs.label}</span>
        <span className="text-[11px] uppercase tracking-wide text-contrast/40">
          {VARIABLE_CATEGORY_LABELS[attrs.category]}
        </span>
      </div>

      <RadioGroup
        value={policy}
        onValueChange={(value) => setPolicy(value as VariableFallbackPolicy)}
        className="flex flex-col gap-2"
      >
        {allowBlockEnrollment && (
          <label
            htmlFor="variable-fallback-block"
            className="flex cursor-pointer items-start gap-2 text-sm text-foreground"
          >
            <RadioGroupItem
              id="variable-fallback-block"
              value="block_enrollment"
              className="mt-0.5"
            />
            <span className="flex flex-col gap-0.5">
              <span className="text-foreground">
                Block enrollment if missing
              </span>
              <span className="text-[11px] text-contrast/50">
                Default. The candidate is moved to the Fix Issues bucket at
                enrollment time.
              </span>
            </span>
          </label>
        )}

        <label
          htmlFor="variable-fallback-literal"
          className="flex cursor-pointer items-start gap-2 text-sm text-foreground"
        >
          <RadioGroupItem
            id="variable-fallback-literal"
            value="literal_fallback"
            className="mt-0.5"
          />
          <span className="flex flex-1 flex-col gap-1">
            <span className="text-foreground">Use a fallback string</span>
            {policy === "literal_fallback" ? (
              <Input
                aria-label="Fallback text"
                placeholder="Fallback text (e.g. there)"
                value={literal}
                onChange={(event) => setLiteral(event.target.value)}
                maxLength={FALLBACK_LITERAL_MAX}
                size="sm"
                variant="muted"
              />
            ) : (
              <span className="text-[11px] text-contrast/50">
                Render a fixed string when the candidate value is missing.
              </span>
            )}
          </span>
        </label>
      </RadioGroup>

      <div className="flex items-center justify-between border-t border-contrast/6 pt-2">
        <Button
          type="button"
          variant="secondary"
          size="xs"
          onClick={onRemove}
          icon={<Icon name="IconTrashCan" size={13} className="mr-0.5" />}
        >
          Remove variable
        </Button>
        <SubmitButton
          type="button"
          variant="primary"
          size="xs"
          disabled={!canSave}
          loading={saving}
          loadingLabel="Saving"
          onClick={handleSave}
        >
          Save
        </SubmitButton>
      </div>
    </div>
  );
}
